import {
  Alert,
  Flex,
  Heading,
  IconButton,
  IconXLine,
  Link,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import React, { useContext, useEffect, useState } from "react";
import "react-loading-skeleton/dist/skeleton.css";
import { ProgramService } from "../../../services/ProgramService.js";
import { SubjectService } from "../../../services/SubjectService.js";
import { UserDetailContext } from "../../../utils/Context.js";
import {
  ERROR,
  useNotification,
} from "../../CustomHooks/NotificationProvider.js";
import { PLOHierarchySelect } from "../../Selectors/AcademicHierarchySelect/HierarchySelector.js";
import PLOSubjectSelect from "./PLOSubjectSelect.js";
import { ProgramLevels } from "./ProgramLevels.js";

export const ProgramLearningOutcomes = () => {
  const { t } = useTranslation();
  const userDetails = useContext(UserDetailContext);
  const showNotification = useNotification();
  const [academicSubjects, setAcademicSubjects] = useState([]);
  const [componentDataIsInitialized, setComponentDataIsInitialized] =
    useState(true);
  const [hierarchySubjects, setHierarchySubjects] = useState([]);
  const [hierarchyId, setHierarchyId] = useState(null);
  const [acLevelsWithSubjects, setAcLevelsWithSubjects] = useState(null);
  const [selectedAcSub, setSelectedAcSub] = useState("");
  const [programs, setPrograms] = useState([]);
  const [levels, setLevels] = useState([]);
  const [acSubClear, setAcSubClear] = useState(false);
  const [acHierarchyClear, setAcHierarchyClear] = useState(false);

  useEffect(() => {
    SubjectService.getSubjects()
      .then((academicSubjects) => {
        setAcademicSubjects(academicSubjects);
      })
      .catch(() =>
        showNotification(
          t("notifications.general.fetchError", { obj: "Departments" }),
          ERROR
        )
      );
  }, []);

  useEffect(() => {
    if (hierarchySubjects.length === 1) {
      onCourseCodeSelect(hierarchySubjects[0]);
    }
  }, [hierarchySubjects]);

  const onCourseCodeSelect = (value) => {
    setAcSubClear(false);
    setComponentDataIsInitialized(false);
    setSelectedAcSub(value);
    ProgramService.getProgramsByAcSub(value).then((programs) => {
      const ploTemp = [];
      if (hierarchyId) {
        const obj = {};
        obj[hierarchyId] = [];
        ploTemp.push(obj);
      }
      programs.forEach((prog) => {
        const obj = {};
        obj[prog.id] = [];
        ploTemp.push(obj);
      });
      setPrograms(programs);
      setComponentDataIsInitialized(true);
    });
  };

  const handleHierarchySelect = (hierarchyId, options) => {
    setPrograms([]);
    setAcHierarchyClear(false);
    const subjectList = [];
    let level;
    if (options) {
      level = options;
    } else {
      level = acLevelsWithSubjects.find((lvl) => lvl.id === hierarchyId);
    }
    level.children.forEach((lvl) => {
      if (lvl.label === "is academic subject") {
        if (userDetails.isAC()) {
          subjectList.push(lvl.id);
        } else {
          const subObj = academicSubjects.find((sub) => sub.name === lvl.name);
          if (userDetails.ac_subjects.includes(subObj.title)) {
            subjectList.push(subObj.id);
          }
        }
      }
    });
    if (subjectList.length > 1) {
      setAcSubClear(true);
    } else {
      setAcSubClear(false);
    }
    setHierarchySubjects(subjectList);
    setSelectedAcSub("");
    setHierarchyId(hierarchyId);
    setLevels([level]);
    const ploTemp = [];
    const obj = {};
    obj[hierarchyId] = [];
    ploTemp.push(obj);
    setComponentDataIsInitialized(true);
  };

  const handleClear = () => {
    setAcHierarchyClear(true);
    setHierarchyId(null);
    setAcSubClear(true);
    setHierarchySubjects([]);
    setSelectedAcSub("");
    setPrograms([]);
    setLevels([]);
  };

  return (
    <>
      <Heading as="h2" level="h3" border="bottom">
        {t("plo.heading")}
      </Heading>
      <Flex
        justifyItems="space-between"
        alignItems="end"
        gap="xx-small"
        margin="small 0"
        wrap="wrap"
      >
        <Flex.Item>
          <Flex alignItems="end" gap="xx-small" margin="small 0 0 0">
            <Flex.Item width={300}>
              <PLOHierarchySelect
                academicSubjects={academicSubjects}
                selectHandler={handleHierarchySelect}
                setAcLevelsWithSubjects={setAcLevelsWithSubjects}
                clear={acHierarchyClear}
              />
            </Flex.Item>
            <Flex.Item>
              <IconButton
                screenReaderLabel={t("programs.clearSubjectOption")}
                renderIcon={IconXLine}
                onClick={handleClear}
              />
            </Flex.Item>
          </Flex>
        </Flex.Item>
        <Flex.Item>
          <Flex alignItems="end" gap="xx-small" margin="small 0 0 0">
            <Flex.Item width={300}>
              <PLOSubjectSelect
                academicSubjects={academicSubjects}
                selectHandler={onCourseCodeSelect}
                defaultSubject={
                  hierarchySubjects.length === 1 ? hierarchySubjects[0] : ""
                }
                hierarchySubjects={hierarchySubjects}
                clear={acSubClear}
              />
            </Flex.Item>
          </Flex>
        </Flex.Item>
      </Flex>
      {acLevelsWithSubjects && acLevelsWithSubjects.length === 0 && (
        <Alert variant="info">
          {t("programs.hierarchyNotSetUp")}{" "}
          <Link href="hierarchy">{t("programs.clickHere")}</Link>{" "}
          {t("programs.toSetUp")}
        </Alert>
      )}
      {componentDataIsInitialized &&
        !!selectedAcSub &&
        ((hierarchyId && programs.length === 0) || programs.length === 0) && (
          <Alert variant="info">{t("programs.noProgramsForSub")}</Alert>
        )}
      {componentDataIsInitialized && !selectedAcSub && hierarchyId && (
        <Alert variant="info">{t("plo.selectAcSub")}</Alert>
      )}
      <ProgramLevels
        selectedAcSub={selectedAcSub}
        programs={[...levels, ...programs]} // rename this variable eventually
        relevantDataIsInitialized={componentDataIsInitialized}
      />
    </>
  );
};
